import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Feather, Moon, Sun } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { dropArticles } from "./dropContent";

const DropDetailPage = ({ nightMode, setNightMode }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = dropArticles[slug];
  const [activeAnchor, setActiveAnchor] = useState("introduction");
  const [lightboxImage, setLightboxImage] = useState(null);
  const sectionRefs = useRef({});

  const structuredSections = useMemo(() => {
    if (!article) return [];

    const introSection = {
      id: "introduction",
      title: "Introduction",
      paragraphs: article.intro.paragraphs,
      figures: article.intro.figures,
    };

    const bodySections = article.sections.map((section) => ({
      id: section.id,
      title: section.title,
      paragraphs: section.paragraphs,
      figures: section.figures,
    }));

    const conclusionSection = {
      id: "conclusion",
      title: "Conclusion",
      paragraphs: article.conclusion.paragraphs,
      figures: article.conclusion.figures,
    };

    return [introSection, ...bodySections, conclusionSection];
  }, [article]);

  const tocItems = useMemo(
    () => structuredSections.map((section) => ({ id: section.id, label: section.title })),
    [structuredSections]
  );

  useEffect(() => {
    if (!article) return;

    const previousTitle = document.title;
    document.title = `The Drop — ${article.hero.title}`;
    return () => {
      document.title = previousTitle;
    };
  }, [article]);

  useEffect(() => {
    if (!structuredSections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visibleEntry) {
          setActiveAnchor(visibleEntry.target.id);
        }
      },
      {
        rootMargin: "-40% 0px -45% 0px",
        threshold: [0.25, 0.5, 0.75],
      }
    );

    const targets = Object.values(sectionRefs.current);
    targets.forEach((el) => el && observer.observe(el));

    return () => {
      targets.forEach((el) => el && observer.unobserve(el));
      observer.disconnect();
    };
  }, [structuredSections]);

  const handleAnchorClick = (id) => {
    const element = sectionRefs.current[id];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (!article) {
    return (
      <div className={`drop-page ${nightMode ? "night" : ""}`}>
        <Navigation activeSection="drop" onNavigate={() => {}} nightMode={nightMode} />
        <div className="drop-empty">
          <div className="drop-empty-card">
            <p>We could not find that story yet.</p>
            <button onClick={() => navigate("/drop")}>Back to The Drop</button>
          </div>
        </div>
        <Footer nightMode={nightMode} />
      </div>
    );
  }

  const metaItems = [
    { label: "Read", value: article.hero.readTime },
    { label: "Theme", value: article.metadata.theme },
    { label: "Location", value: article.metadata.location },
  ];

  return (
    <div className={`drop-page ${nightMode ? "night" : ""}`}>
      <div className="drop-gradient-backdrop" />
      <Navigation activeSection="drop" onNavigate={() => {}} nightMode={nightMode} />

      <main className="drop-main">
        <section className="drop-hero">
        <div className="drop-hero-ornament drop-hero-ornament--left" />
        <div className="drop-hero-ornament drop-hero-ornament--right" />
        <motion.div
          className="drop-hero-meta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="drop-hero-meta-left">
            <button className="drop-hero-back" onClick={() => navigate("/drop")}
              aria-label="Back to The Drop index">
              <ArrowLeft size={16} />
              Back to all drops
            </button>
            <span className="drop-hero-kicker">{article.hero.kicker}</span>
          </div>
          <div className="drop-hero-tools">
            <span className="drop-hero-pill">
              <Feather size={14} />
              {article.hero.readTime}
            </span>
            <button
              type="button"
              className="drop-hero-toggle"
              onClick={() => setNightMode(!nightMode)}
            >
              {nightMode ? <Sun size={16} /> : <Moon size={16} />}
              <span>{nightMode ? "Day" : "Night"} mode</span>
            </button>
          </div>
        </motion.div>

        <motion.h1
          className="drop-hero-title"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
        >
          {article.hero.title}
        </motion.h1>

        <motion.p
          className="drop-hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.12 }}
        >
          {article.hero.subtitle}
        </motion.p>

        <motion.div
          className="drop-hero-meta-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18 }}
        >
          {metaItems.map((item) => (
            <div className="drop-meta-card" key={item.label}>
              <span className="drop-meta-label">{item.label}</span>
              <span className="drop-meta-value">{item.value}</span>
            </div>
          ))}
        </motion.div>
      </section>

      <section className="drop-insights">
        {article.insights.map((insight) => (
          <motion.div
            key={insight.label}
            className="drop-insight-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.4 }}
          >
            <span className="drop-insight-value">{insight.value}</span>
            <span className="drop-insight-label">{insight.label}</span>
            <p>{insight.description}</p>
          </motion.div>
        ))}
      </section>

      <div className="drop-layout">
        <aside className="drop-toc">
          <div className="drop-toc-inner">
            <p className="drop-toc-label">Chapters</p>
            <div className="drop-toc-divider" />
            {tocItems.map((item) => (
              <button
                key={item.id}
                className={`drop-toc-button ${activeAnchor === item.id ? "active" : ""}`}
                onClick={() => handleAnchorClick(item.id)}
              >
                <span>{item.label}</span>
                <ArrowUpRight size={14} />
              </button>
            ))}
          </div>
        </aside>

        <article className="drop-article">
          {structuredSections.map((section, index) => (
            <section
              key={section.id}
              id={section.id}
              className="drop-section"
              ref={(el) => {
                sectionRefs.current[section.id] = el;
              }}
              data-drop-section
            >
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6 }}
              >
                <h2>{section.title}</h2>
                <div className="drop-section-body">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </motion.div>

              <AnimatePresence>
                {section.figures?.map((figure) => (
                  figure.image ? (
                    <motion.figure
                      key={figure.caption}
                      className="drop-figure"
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.35 }}
                      transition={{ duration: 0.6, delay: 0.05 }}
                    >
                      <img
                        src={figure.image}
                        alt={figure.caption}
                        loading="lazy"
                        onClick={() => setLightboxImage({ src: figure.image, caption: figure.caption, source: figure.source })}
                        style={{ cursor: 'pointer' }}
                      />
                      <figcaption>
                        <span>{figure.caption}</span>
                        {figure.source ? <em>{figure.source}</em> : null}
                      </figcaption>
                    </motion.figure>
                  ) : null
                ))}
              </AnimatePresence>

              {article.pullQuotes[index] ? (
                <motion.aside
                  className="drop-pull-quote"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="drop-quote-mark">“</span>
                  <p>{article.pullQuotes[index]}</p>
                </motion.aside>
              ) : null}
            </section>
          ))}
        </article>
      </div>

      <section className="drop-gallery">
        <div className="drop-gallery-header">
          <h3>Visual Index</h3>
        </div>
        <div className="drop-gallery-track">
          {article.gallery.map((item) => (
            <motion.div
              key={item.image}
              className="drop-gallery-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5 }}
            >
              <div
                className="drop-gallery-frame"
                onClick={() => setLightboxImage({ src: item.image, caption: item.caption, source: item.source })}
                style={{ cursor: 'pointer' }}
              >
                <img src={item.image} alt={item.caption} loading="lazy" />
              </div>
              <div className="drop-gallery-caption">
                <span>{item.caption}</span>
                {item.source ? <em>{item.source}</em> : null}
              </div>
            </motion.div>
          ))}
        </div>
      </section>


      <section className="drop-references">
        <div className="drop-references-dropdown">
          <h3 className="drop-references-trigger">References & Further Reading</h3>
          <div className="drop-references-content">
            <div className="drop-reference-list">
              {article.references.map((reference, index) => {
                const urlMatch = reference.match(/(https?:\/\/[^\s]+)/);
                const url = urlMatch ? urlMatch[0] : null;

                if (url) {
                  const parts = reference.split(url);
                  return (
                    <p key={index}>
                      {parts[0]}
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                      </a>
                      {parts[1] || ''}
                    </p>
                  );
                }

                return <p key={index}>{reference}</p>;
              })}
            </div>
          </div>
        </div>
      </section>
      </main>

      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            className="drop-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightboxImage(null)}
          >
            <motion.div
              className="drop-lightbox-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="drop-lightbox-close"
                onClick={() => setLightboxImage(null)}
                aria-label="Close"
              >
                ✕
              </button>
              <img src={lightboxImage.src} alt={lightboxImage.caption} />
              {(lightboxImage.caption || lightboxImage.source) && (
                <div className="drop-lightbox-caption">
                  {lightboxImage.caption && <span>{lightboxImage.caption}</span>}
                  {lightboxImage.source && <em>{lightboxImage.source}</em>}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer nightMode={nightMode} />
    </div>
  );
};

export default DropDetailPage;
